/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, RefObject } from 'react'
import { isIOS, isMobile } from 'react-device-detect'
import { Icon, Popover, Radio } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import _ from 'lodash'
import dayjs from 'dayjs'
import AdvCasinoBannerButton from '@components/Button/AdvCasinoBannerButton'
import useRootStore from '@store/useRootStore'
import { EmoticonArea, ModalBg } from '@styles/base.style'
import { Button } from '@styles/button.style'
import { GlobalStore } from '@store/globalStore'
import { EmoticonIco, SettingIco } from '@shared/Icon/Chat'
import { BettingHistoryIco } from '@shared/Icon/MenuIcon'
import ImgUploadIco from '@assets/images/icon/chat_icon/img-upload-ico.png'
import { AuthStore } from '@store/authStore'
import { ChattingStore } from '@store/chattingStore'
import { makeActivityGageConfig, maxImageFileSizeValidate, showToast, TOAST_TYPE } from '@utils/utils'
import MessageContentPopup from '@views/Popup/MessageContentPopup'
import { MessageStore } from '@store/messageStore'
import styled from '@themes/theme'
import { CHAT_TAB_INDEX_KEY } from '@constants/index'
import { ChatMessageType, SendMessageToEnterType } from '@interface/chat'
import { CHAT_CONTENT_BUTTON_TYPE, FROM_VIEW_TYPE, GlobalStoreKey, USER_PENALTY_TYPE } from '@v2/constants/common'
import { CompanyPickStore } from '@store/companyPickStore'
import { BettingHistoryQuickMenu } from '@views/BettingHistory/BettingHistoryListItem/index.style'
// import SelectLiveMatch from '@views/Popup/Chat/ChatLiveScore/SelectLiveMatch'
import Login from '@views/Login'
import SimpleBettingHistory from '@components/SimpleBettingHistory'
import { BettingHistoryStore } from '@store/bettingHistoryStore'
import ChatTab from './ChatTab'
import {
  ChatFavoriteMatchBtnStyle,
  ChatFloatingBtnStyle,
  ChatLoginBtn,
  ChatQuickSpotsMenuBtnStyle,
  ChatToggleBtn,
  ChatWrap,
  CheerForTeamEmoticonListStyle,
} from './index.style'
import ChatLiveScore from './ChatLiveScore'
import PickNoticePopup from './PickNoticePopup'
import StorageUtils from '@utils/storage-utils'
import PopupUtils from '@utils/popup-utils'
import { AdvButton } from '@components/Button'
import { NewBadgeIco } from '@shared/Icon/EtcIcon'
import { UserPickStore } from '@store/userPickStore'
import { SimpleCheerForTeamMatchInfo } from 'components/SimpleCheerForTeamMatchInfo'
import DialogUtils from '@utils/dialog-utils'
import { CheerForTeam } from '@views/Popup/Chat/CheerForTeam'
import classNames from 'classnames'
import TimeIco from '@shared/Icon/MatchupIcon/TimeIco'
import { PenaltyStore } from '@store/penaltyStore'
import { UserStore } from '@store/userStore'
import * as ImageUtils from '@utils/image-utils'
import { LogStore } from '@store/logStore'
import { ActivityScoreNoticePopup } from '@views/Popup/ActivityScoreNoticePopup'
import { USER_ACTIVITY_TYPE } from '@v2/constants/activity'
import AdvImg from '@components/AdvImg'
import { PopularSearchTerm } from 'components/PopularSearchTerm'
import { PopularSearchTermStore } from '@store/popularSearchTermStore'

interface ChatProps {
  chattingStore: ChattingStore
  authStore: AuthStore
  globalStore: GlobalStore
  messageStore: MessageStore
  companyPickStore: CompanyPickStore
  userPickStore: UserPickStore
  penaltyStore: PenaltyStore
  userStore: UserStore
  logStore: LogStore
  pcChatWrapClass?: string
  chatBtnClass?: string
  bettingHistoryStore: BettingHistoryStore
  availableScoreTime?: number
  popularSearchTermStore: PopularSearchTermStore
}

interface ChatComponentState {
  chatWrapClass: string
  chatToggleBtnClass: string
  visiblePicker: boolean
  file: string
  previewURL: string
  textValues: string
  sendBtnClass: string
  showEmoticonClass: string
  emoticonAreaClass: string
  pickWrapClass: string
  showKeyboard: boolean
  keyboardHeight: number
  chatInputTimout: any
  chattingInputKey: string
  emoticonTimeoutID: any
  touchTimer: any
  bubbleVisible: boolean
  showBettingHistory: boolean
  isChatSending: boolean
  isChatAttachPreview: boolean
}

const PreviewTextMessageWrap = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: bottom;
  width: calc(100% - 32px - 46px);
  overflow: hidden;
  line-height: 1.5;
  font-weight: 700;
  &.pc {
    width: calc(100% - 32px - 28px);
  }
`

@observer
class Chat extends React.PureComponent<ChatProps, ChatComponentState> {

  OPEN_CHAT_WRAP_CLASS = 'open-chat-wrap'
  CLOSE_CHAT_WRAP_CLASS = 'close-chat'

  private readonly globalStore: GlobalStore

  private readonly companyPickStore: CompanyPickStore

  // emoticon double click millisecond
  private readonly emoticonDoubleClickTimeout: number

  state: ChatComponentState = {
    chatWrapClass: '',
    chatToggleBtnClass: this.CLOSE_CHAT_WRAP_CLASS,
    visiblePicker: false,
    file: '',
    previewURL: '',
    textValues: '',
    sendBtnClass: 'send-btn',
    showEmoticonClass: 'emoticon-btn',
    emoticonAreaClass: 'emoticon-area',
    pickWrapClass: 'pick-wrap',
    showKeyboard: false,
    keyboardHeight: 288,
    chatInputTimout: undefined,
    chattingInputKey: 'chatting-input-textarea',
    emoticonTimeoutID: null,
    touchTimer: undefined,
    bubbleVisible: false,
    showBettingHistory: false,
    isChatSending: false,
    isChatAttachPreview: false,
  }

  chatInputRef: RefObject<TextArea>
  chatInputAreaRef: RefObject<HTMLDivElement>
  chatInputTimeout = undefined
  imageFileInputRef: RefObject<HTMLInputElement>

  constructor(props: ChatProps) {
    super(props)
    this.emoticonDoubleClickTimeout = 200
    this.globalStore = props.globalStore
    this.companyPickStore = props.companyPickStore
    this.chatInputRef = React.createRef<TextArea>()
    this.chatInputAreaRef = React.createRef<HTMLDivElement>()
    this.imageFileInputRef = React.createRef<HTMLInputElement>()
    const kHeight = StorageUtils.getKeyboardHeight()
    if (kHeight) {
      this.state.keyboardHeight = Number(kHeight)
    }
  }

  // custom
  handleFileOnChange = (event: any) => {
    event.preventDefault()
    const { chattingStore } = this.props
    const reader = new FileReader()
    const file = event.target.files[0]
    if (maxImageFileSizeValidate(file)) { // TODO 첨부 이미지 최대 용량을 글로벌로 관리 해도 괜찮을까?
      reader.onload = () => {
        chattingStore.uploadImage(file).then((uploadedImageName: string): void => {
          this.setState({
            file,
            previewURL: uploadedImageName,
          })
        })
      }
      reader.readAsDataURL(file)
    }
    this.globalStore.setIgnoreVisibleChangeHandler(false)
    this.resetImageFileInput()
  }

  componentDidUpdate(prevProps: Readonly<ChatProps>, prevState: Readonly<object>, snapshot?: any) {
    if (this.state.showKeyboard && this.chatInputRef.current) {
      this.chatInputRef.current.focus()
    }
  }

  componentDidMount() {
    document.addEventListener('paste', this.imagePasteHandler, true)
  }

  componentWillUnmount() {
    document.removeEventListener('paste', this.imagePasteHandler, true)
  }

  imagePasteHandler = (e) => {
    const { chattingStore } = this.props
    const items = (e.clipboardData || e.originalEvent.clipboardData).items
    if (_.size(items) > 0) {
      const item = items[0]
      const file = item.getAsFile()
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        chattingStore.uploadImage(file).then((uploadedImageName: string) => {
          this.setState({
            file,
            previewURL: uploadedImageName,
          })
        })
      }
      reader.readAsDataURL(file)
    }
  }

  resetImageFileInput = () => {
    if (this.imageFileInputRef.current) {
      this.imageFileInputRef.current.value = ''
    }
  }

  setEmoticonTimeout = (value) => {
    this.setState({
      emoticonTimeoutID: value,
    })
  }

  render() {
    const {
      authStore,
      globalStore,
      chattingStore,
      messageStore,
      chatBtnClass,
      pcChatWrapClass,
      bettingHistoryStore,
      userPickStore,
      penaltyStore,
      userStore,
      logStore,
      popularSearchTermStore,
    } = this.props
    const { visibleSendMessagePopup } = globalStore
    const { receiveUser } = messageStore
    const { popularSearchTerms } = popularSearchTermStore

    const replyTargetUser = { id: receiveUser?.id, nick: receiveUser?.nick, title: '' }
    const {
      chatToggleBtnClass, visiblePicker, textValues, sendBtnClass,
      file, showEmoticonClass, emoticonAreaClass, chattingInputKey,
      chatWrapClass, pickWrapClass, showKeyboard,
      /*keyboardHeight,*/
      bubbleVisible, isChatAttachPreview,
    } = this.state
    const {
      uploadedImageSrc, selectedEmoticonUrl,
      emoticonList, replyToUser, isVisibleChattingModal, currentTabIndexKey,
      newChatMessage, chattingOptions, errorMessage, isVisibleChatContent,
      selectedCheerForTeamItem, isVisibleCheerForTeamPanel,
    } = chattingStore

    const { selectedBettingHistory } = bettingHistoryStore
    const { userInfo = {} } = authStore
    const { cheerForTeamUserActivityScoreLastTime } = userStore

    const imageUrl = globalStore.findGlobalSetting(GlobalStoreKey.INGAME_IMAGES_URL).value
    const chatIconImgUrl = `${imageUrl}icon/chat_icon/chat_icon.png`
    const cheerBtnImgUrl = `${imageUrl}btn/cheer_btn.png`
    const TodayUserPickBtnImgUrl = `${imageUrl}icon/chat_icon/today_user_pick_btn.png`
    let ActivityScoreIconImgUrl
    if (globalStore.userActivityInfo?.activityRewardAvailable) {
      ActivityScoreIconImgUrl = `${imageUrl}btn/activity_score_icon_active.png`
    } else {
      ActivityScoreIconImgUrl = `${imageUrl}btn/activity_score_icon.png`
    }

    const effectIconList = ImageUtils.getCheerForTeamEffectIconList()
    const chatContentButtonOptions = globalStore.getChatContentButtonOptions()

    const isCheerForTeamActive = chattingOptions.activeCheerForTeam
    const isChatCheerForTeamButtonEnabled = _.find(chatContentButtonOptions,
      { buttonType: CHAT_CONTENT_BUTTON_TYPE.CHEER_FOR_TEAM })?.enabled || false

    if (showKeyboard) {
      const screenHeight = StorageUtils.getScreenHeight()
      if (screenHeight) {
        const kHeight = Number(screenHeight) - window.innerHeight
        if (kHeight) {
          StorageUtils.saveKeyboardHeight(kHeight)
          this.setState({ keyboardHeight: kHeight })
        }
      }
    }

    if (isMobile && !isVisibleChatContent && this.chatInputRef.current) {
      this.chatInputRef.current.blur()
    }

    if (errorMessage) {
      this.setState({
        textValues: errorMessage,
      })
      setTimeout(() => {
        chattingStore.setChatErrorMessage(undefined)
        this.setState({
          textValues: '',
        })
      }, 3000)
    }

    const onVisibleChange = (visible: boolean) => {
      if (!authStore.isSignedIn) {
        authStore.showLoginPopup(<Login/>)
        return
      }
      this.setState({
        bubbleVisible: visible,
      })
    }

    const nickname = userInfo.nick
    const emoticonUrlPrefix = globalStore.findGlobalSetting(GlobalStoreKey.CHAT_EMOTICON_URL_PREFIX)?.value
    const chatImageUrlPrefix = globalStore.findGlobalSetting(GlobalStoreKey.CHAT_ATTACH_IMAGE_URL_PREFIX)?.value

    const setOpenChatWrapClass = () => {
      let openChatWrapClass = this.OPEN_CHAT_WRAP_CLASS

      // ios에서 키보드 패널 활성화시 SafeAreaEnvPadding 값이 발생하지 않도록 추가한 코드입니다. [이슈링크](https://task.zempublic.co.kr/view.php?id=10390)
      if (this.state.showKeyboard && isMobile && isIOS) {
        openChatWrapClass += ' is-open-keyboard'
      }

      if (this.state.chatWrapClass === openChatWrapClass) {
        return
      }

      this.setState({
        chatWrapClass: openChatWrapClass,
        chatToggleBtnClass: 'open',
      })
    }

    const setCloseChatWrapClass = () => {
      if (this.state.chatWrapClass === '') {
        return
      }

      this.setState({
        chatWrapClass: '',
        chatToggleBtnClass: this.CLOSE_CHAT_WRAP_CLASS,
        showEmoticonClass: 'emoticon-btn',
        emoticonAreaClass: 'emoticon-area',
      })
    }

    const updateChatClass = () => {
      if (isMobile && !isVisibleChattingModal) {
        setCloseChatWrapClass()
        return
      }
      setOpenChatWrapClass()
    }

    updateChatClass()

    const onClickToggleChat = () => {
      if (isVisibleChattingModal) {
        logStore.writeButtonLog({ pageId: 'h010', buttonId: '002' }).catch(console.error)
      } else {
        logStore.writeButtonLog({ pageId: 'h010', buttonId: '001' }).catch(console.error)
      }
      if (isMobile) {
        chattingStore.changeChattingModalVisible(!isVisibleChattingModal).catch(console.error)
        userPickStore.fetchLastBestUserPickId().catch(console.error)
      } else {
        chattingStore.changeChattingModalVisible(true).catch(console.error)
      }
    }

    const onClickTogglePicker = () => {
      globalStore.clearActivityScoreNoticeQueue()
      if (!chattingOptions.activeImageAttach && !chattingOptions.activeBetHistoryAttach) {
        return
      }

      if (!visiblePicker) {
        globalStore.setIgnoreVisibleChangeHandler(false)
      }

      this.setState({
        visiblePicker: !visiblePicker,
      })
    }

    const onClickClosePreview = () => {
      chattingStore.clearState()
      bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.CHAT)
      const state = {
        file: '',
        previewURL: '',
        sendBtnClass: 'send-btn',
        showBettingHistory: false,
        isChatAttachPreview: false,
      }
      if (textValues) {
        delete state.sendBtnClass
      }
      this.setState(state)
      this.resetImageFileInput()
    }

    /** 응원글 미리보기 닫기버튼 */
    const onClickCheerForTeamClosePreview = () => {
      chattingStore.setSelectedCheerForTeamItem(null)
    }

    const isShowEmoticonPanel = () => {
      return this.state.showEmoticonClass !== 'emoticon-btn' && this.state.emoticonAreaClass !== 'emoticon-area'
    }

    const hideEmoticonsPanel = () => {
      this.setState({
        showEmoticonClass: 'emoticon-btn',
        emoticonAreaClass: 'emoticon-area',
      })
    }

    const isChatActive = chattingOptions.active
    if (isVisibleChattingModal && !isChatActive) {
      chattingStore.changeChattingModalVisible(false).catch(console.error)
      hideEmoticonsPanel()
    }

    const onClickBettingListShareButton = () => {
      this.chatInputRef.current.blur()
      hideEmoticonsPanel()
      this.setState({
        showBettingHistory: true,
      })
      bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.CHAT)
      bettingHistoryStore.setBettingHistoryInitProps({ onlyMajorClub: true, fromViewType: FROM_VIEW_TYPE.CHAT })
      globalStore.setVisibleBettingListPopup(true, true, true)
    }

    // 공유된 베팅 내역 이벤트
    const onClickOpenBettingListInfo = () => {
      DialogUtils.showSharedBettingList({
        bettingRecordTrack: selectedBettingHistory[FROM_VIEW_TYPE.CHAT],
        fromUserNick: nickname
      })
    }

    const sendChatMessage = async (): Promise<void> => {
      let penaltyCheckResult = await penaltyStore.checkUserPenalty(USER_PENALTY_TYPE.CHATTING)

      if (_.isEmpty(penaltyCheckResult) && selectedCheerForTeamItem) {
        penaltyCheckResult = await penaltyStore.checkUserPenalty(USER_PENALTY_TYPE.CHEER_FOR_TEAM)
      }

      if (!_.isEmpty(penaltyCheckResult)) {
        DialogUtils.showPenaltyInfo(penaltyCheckResult)
      } else if (chattingStore.checkChatFlood()) {
        const result = await penaltyStore.autoApplyChatFloodPenalty()

        if (result?.isWarning) {
          DialogUtils.showChatPenaltyInfo()
        } else if (result?.isPenalty) {
          const { penaltyMessage } = result
          DialogUtils.showPenaltyInfo(penaltyMessage)
        }
      } else {
        chattingStore.sendChatMessage(textValues).catch(console.error)
      }

      cleanUpChatState()

      setTimeout(() => {
        if (!isMobile) {
          this.chatInputRef.current.focus()
        }
      }, 10)
    }

    const onClickSendChatMessage = (): void => {
      if (this.state.isChatSending) {
        return
      }

      if (!uploadedImageSrc
        && !selectedEmoticonUrl
        && !bettingHistoryStore.selectedBettingHistory[FROM_VIEW_TYPE.CHAT]
        && !textValues
      ) {
        return
      }

      this.setState({ isChatSending: true })

      globalStore.setIgnoreVisibleChangeHandler(false)
      sendChatMessage().catch(console.error).finally(() => {
        setTimeout(() => {
          this.setState({ isChatSending: false })
        }, 10)
      })
    }

    let availableUserActivityScore = false
    let remainingUserActivityScoreTime = '0'

    if (!_.isEmpty(cheerForTeamUserActivityScoreLastTime)) {
      const delaySeconds = globalStore.findGlobalSetting(GlobalStoreKey.USER_ACTIVITY_SCORE_DELAY_TIME)?.value
      if (!_.isEmpty(delaySeconds)) {
        const remainingTime = dayjs().diff(cheerForTeamUserActivityScoreLastTime, 'seconds')
        if (remainingTime < Number(delaySeconds)) {
          const lastTime = dayjs(cheerForTeamUserActivityScoreLastTime).add(Number(delaySeconds), 'seconds')
          const duration = dayjs.duration(dayjs().diff(lastTime))
          if (duration.minutes()) {
            remainingUserActivityScoreTime = `${duration.minutes() * -1}분`
          } else if (duration.seconds()) {
            remainingUserActivityScoreTime = `${duration.seconds() * -1}초`
          }
        }
      }
    }

    if (remainingUserActivityScoreTime === '0') {
      availableUserActivityScore = true
    }

    let deletePreviewBtn = <Button onClick={onClickClosePreview} className="delete-img-btn"><span className="x-ico"/></Button>
    let previewWrapClassName = 'img-preview'
    let previewContents: React.ReactElement
    let cheerForTeamPreviewContents: React.ReactElement
    if (file !== '') {
      if (bettingHistoryStore.selectedBettingHistory[FROM_VIEW_TYPE.CHAT]) {
        bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.CHAT)
      }
      previewContents = (
        <Button TP onClick={onClickSendChatMessage}>
          <AdvImg src={`${chatImageUrlPrefix}${this.state.previewURL}`} srcMode={'small'} alt="이미지 미리보기" className="upload-img-preview lhn5"/>
        </Button>
      )
      const state = {
        visiblePicker: false,
        sendBtnClass: 'send-btn',
        isChatAttachPreview: true,
      }
      if (uploadedImageSrc) {
        state.sendBtnClass += ' active'
      }
      this.setState(state)
    } else if (selectedBettingHistory[FROM_VIEW_TYPE.CHAT] && this.state.showBettingHistory) {
      const clonedSelectedBettingHistory = _.cloneDeep(selectedBettingHistory[FROM_VIEW_TYPE.CHAT])
      if (bettingHistoryStore.selectedBettingHistoryFoldData[FROM_VIEW_TYPE.CHAT]) {
        clonedSelectedBettingHistory.bet['displayFolds'] = []
        clonedSelectedBettingHistory.bet['displayFolds'].push({
          foldId: bettingHistoryStore.selectedBettingHistoryFoldData[FROM_VIEW_TYPE.CHAT]?.foldId,
          selectionName: bettingHistoryStore.selectedBettingHistoryFoldData[FROM_VIEW_TYPE.CHAT]?.selectionName,
          outcomeRate: bettingHistoryStore.selectedBettingHistoryFoldData[FROM_VIEW_TYPE.CHAT]?.outcomeRate,
          outcomeName: bettingHistoryStore.selectedBettingHistoryFoldData[FROM_VIEW_TYPE.CHAT]?.marketName,
          specialBetValue: bettingHistoryStore.selectedBettingHistoryFoldData[FROM_VIEW_TYPE.CHAT]?.specialBetValue,
          hitState: bettingHistoryStore.selectedBettingHistoryFoldData[FROM_VIEW_TYPE.CHAT]?.hitState,
          homeTeamName: bettingHistoryStore.selectedBettingHistoryFold[FROM_VIEW_TYPE.CHAT]?.homeTeamName,
          awayTeamName: bettingHistoryStore.selectedBettingHistoryFold[FROM_VIEW_TYPE.CHAT]?.awayTeamName,
          sportId: bettingHistoryStore.selectedBettingHistoryFold[FROM_VIEW_TYPE.CHAT]?.sportId,
        })
      }
      previewContents = (
        <SimpleBettingHistory
          bettingRecordTrack={clonedSelectedBettingHistory}
          onClickOpenBettingListInfo={onClickOpenBettingListInfo}
          className={isMobile ? 'betting-list' : 'pc-betting-list'}
        />
      )
      this.setState({
        visiblePicker: false,
        sendBtnClass: 'send-btn active',
        isChatAttachPreview: true,
      })
    } else if (selectedEmoticonUrl) {
      if (bettingHistoryStore.selectedBettingHistory[FROM_VIEW_TYPE.CHAT]) {
        bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.CHAT)
      }
      const previewImgSrc = `${emoticonUrlPrefix}${selectedEmoticonUrl}`
      previewContents = (
        <Button TP onClick={onClickSendChatMessage}>
          <img className="upload-img-preview" src={previewImgSrc} alt="이모티콘"/>
        </Button>
      )
      this.setState({
        visiblePicker: false,
        sendBtnClass: 'send-btn active',
        isChatAttachPreview: true,
      })
    } else if (newChatMessage
      && chattingStore.isShowNewMessageForBottomModal(newChatMessage)
      && _.isNull(selectedCheerForTeamItem))
    {
      const previewMessage = chattingStore.makePreviewChatMessage(newChatMessage)
      previewContents = (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', zIndex: '1' }} onClick={() => {
          if (isVisibleChattingModal || !isMobile) {
            if (newChatMessage.type === ChatMessageType.BET_HISTORY) {
              if (currentTabIndexKey === CHAT_TAB_INDEX_KEY.RECEIVED_MESSAGE) {
                chattingStore.changeChattingTabState(CHAT_TAB_INDEX_KEY.ALL)
              }
            } else {
              chattingStore.changeChattingTabState(CHAT_TAB_INDEX_KEY.ALL)
            }
            chattingStore.setDelayChatAreaScrollToBottom()
          }
          // 채팅응원글 패널 닫기
          chattingStore.setVisibleCheerForTeamPanel(false)
        }}>
          <Icon type="down-circle" style={{ margin: '0 5px 0 15px' }}/><PreviewTextMessageWrap className={!isMobile ? 'pc' : ''}>{previewMessage}</PreviewTextMessageWrap>
        </div>
      )
      this.setState({ visiblePicker: false })
    } else if (!_.isNull(selectedCheerForTeamItem)) {
      previewWrapClassName += ''
      deletePreviewBtn = null
      this.setState({ isChatAttachPreview: false })
    } else {
      previewWrapClassName += ' adv-hidden'
      previewContents = undefined
      if (!textValues) {
        this.setState({
          sendBtnClass: 'send-btn',
        })
      }
      this.setState({ isChatAttachPreview: false })
    }

    if (!_.isNull(selectedCheerForTeamItem)) {
      const effectIconId = ImageUtils.findEffectIconId(selectedCheerForTeamItem.effectIconUrl)
      cheerForTeamPreviewContents = (
        <div className="preview-wrap">
          <Radio.Group
            className={isMobile ? 'cheer-for-team-emoticon-list' : 'adv-hidden'}
            onChange={(e) => onClickCheerForTeamEmoticonHandler(e.target.value)}
            defaultValue={ImageUtils.DEFAULT_EFFECT_ICON_ID}
            value={effectIconId}
            css={CheerForTeamEmoticonListStyle}
          >
            {effectIconList.map(item =>
              <Radio.Button
                key={`cheer-for-team_effect-icon-${item.id}`}
                value={item.id}
                className="cheer-for-team-emoticon-list--item"
              >
                <img className="cheer-for-team-emoticon-img" src={item.pvImg} alt={item.name}/>
              </Radio.Button>,
            )}
          </Radio.Group>
          <SimpleCheerForTeamMatchInfo
            selectedCheerForTeamData={selectedCheerForTeamItem}
          />
          {!availableUserActivityScore &&
            <p className="available-time"><TimeIco/>다음 활동점수 획득까지 남은 시간<span className="available-time-num">{remainingUserActivityScoreTime}</span></p>
          }
          <Button onClick={onClickCheerForTeamClosePreview} className="delete-img-btn"><span className="x-ico"/></Button>
        </div>
      )
    }
    // 선택한 응원글이 있을 경우 plusQuickMenuBtn 클릭시 화면에 응원글 미리보기가 출력되지않도록 하기위해 작성하였습니다.
    if (visiblePicker) {
      previewWrapClassName += ' adv-hidden'
    }

    const onClickCheerForTeamEmoticonHandler = (value) => {
      const selectedEffect = effectIconList.find(item => item.id === value)
      chattingStore.updateSelectedCheerForTeamItemEffectUrl(selectedEffect.img)
    }

    const previewWarpChildClassName = classNames({
      'preview-wrap existing': !_.isEmpty(previewContents),
      'preview-wrap': _.isEmpty(previewContents),
    })
    const previewWrap = (
      <div className={previewWrapClassName}>
        {/* 응원글 */}
        {cheerForTeamPreviewContents}
        <div className={previewWarpChildClassName}>
          {/* 미리보기 */}
          {previewContents}
          {/* 미리보기 창 닫기 */}
          {deletePreviewBtn}
        </div>
      </div>
    )

    // if (textValues.length > 0) {
    //   this.setState({
    //     sendBtnClass: 'send-btn active',
    //   })
    // }
    // setTimeout(() => {
    //   if (textValues.length === 0) {
    //     this.setState({
    //       sendBtnClass: 'send-btn',
    //     })
    //   }
    // }, 3000) /* 자판 치는 도중에 계속해서 클래스가 변경되어 setTimeout 설정 (한글자 쓰는 과정중에 발생) */

    const onTextAreaChange = (inputText) => {
      if (inputText?.replace(/\n/gi, '').length > 200) {
        showToast(TOAST_TYPE.WARN, '200자를 초과 하였습니다.')
        this.setState({
          textValues,
        })
        return
      }
      let sendBtnClassName = 'send-btn'
      if (inputText.length > 0 || uploadedImageSrc) {
        sendBtnClassName += ' active'
      }
      this.setState({
        textValues: inputText,
        sendBtnClass: sendBtnClassName,
      })
    }

    if (this.state.showEmoticonClass.indexOf('active') > -1) {
      // 이모티콘이 나올때 스크롤 포지션이 위로 상승하는 현상 해결을 위해...
      if (chattingStore.isChatAreaScrolledToBottom() && !isVisibleCheerForTeamPanel) {
        chattingStore.setDelayChatAreaScrollToBottom()
      }
    }

    const toggleEmoticonsPanel = (hideKeyboard = true) => {
      if (this.state.isChatAttachPreview && !isShowEmoticonPanel()) {
        return
      }

      globalStore.clearActivityScoreNoticeQueue()
      if (emoticonAreaClass === 'emoticon-area') { // 이모티콘 패널 열기
        if (showKeyboard) {
          this.chatInputRef.current.blur()
        }

        const state = {
          showKeyboard: false,
          showEmoticonClass: 'emoticon-btn active',
          emoticonAreaClass: 'emoticon-area show',
        }
        if (visiblePicker) {
          this.setState({
            ...state,
            visiblePicker: false,
            pickWrapClass: 'pick-wrap',
          })
        } else {
          this.setState({
            ...state,
            pickWrapClass: 'pick-wrap',
          })
        }
      } else { // 이모티콘 패널 닫기
        this.setState({
          showEmoticonClass: 'emoticon-btn',
          emoticonAreaClass: 'emoticon-area',
        })
        // 이모티콘 열린 상태에서 채팅 리스트 스크롤 후 닫을 시 채팅 리스트가 위로 올라가있는 현상 해결
        chattingStore.setDelayChatAreaScrollToBottom()
      }
    }

    const cleanUpChatState = () => {
      chattingStore.clearState()
      bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.CHAT)
      chattingStore.setSelectedCheerForTeamItem(null)
      this.setState({
        file: '',
        previewURL: '',
        sendBtnClass: 'send-btn',
        textValues: '',
        chattingInputKey: `${chattingInputKey}_${Date.now()}`,
        showBettingHistory: false,
        isChatAttachPreview: false,
      })
    }

    const onTouchStartImageAttachHandler = () => {
      this.chatInputRef.current.blur()
      this.setState({ showKeyboard: false })
      hideEmoticonsPanel()
      globalStore.setIgnoreVisibleChangeHandler(true)
    }

    const quickMenuListItems = (
      <Fragment>
        {chattingOptions.activeBetHistoryAttach && (
          <Button className="bet-share-btn" onClick={onClickBettingListShareButton} disabled={isChatAttachPreview}>
            <BettingHistoryIco/>
            {isMobile && '베팅내역'}
          </Button>
        )}
        {chattingOptions.activeImageAttach && (
          <div className="upload-img-wrap">
            <img src={ImgUploadIco} alt="이미지 업로드"/>
            {isMobile && '이미지'}
            <input type="file"
                   disabled={isChatAttachPreview}
                   ref={this.imageFileInputRef}
                   accept="image/jpg,image/jpeg,image/png,image/gif,image/bmp,image/tif,image/tiff"
                   name="profile_img"
                   className="upload-img-wrap--input"
                   onTouchStart={onTouchStartImageAttachHandler}
                   onChange={this.handleFileOnChange}/>
          </div>
        )}
      </Fragment>
    )
    const plusQuickMenuBtn = (
      <Button onClick={onClickTogglePicker} className={visiblePicker ? 'select-btn active' : 'select-btn'}>
        <span className="plus-ico"/>
      </Button>
    )
    const emoticonBtn = (
      <Button className={showEmoticonClass} onClick={() => toggleEmoticonsPanel()}
              disabled={isChatAttachPreview && !isShowEmoticonPanel()}>
        <EmoticonIco/>
      </Button>
    )
    const sendBtn = (
      <Button className={sendBtnClass} onClick={onClickSendChatMessage}>
        <div className="send-ico">
          <span className="send-ico--arrow"/>
        </div>
      </Button>
    )

    if (emoticonAreaClass === 'emoticon-area') {
      if (visiblePicker) {
        this.setState({
          pickWrapClass: 'pick-wrap open',
        })
      } else {
        this.setState({
          pickWrapClass: 'pick-wrap',
        })
      }
    } else if (visiblePicker) {
      this.setState({
        pickWrapClass: 'pick-wrap emoticon-open open',
      })
    } else {
      this.setState({
        pickWrapClass: 'pick-wrap emoticon-open',
      })
    }

    const onClickEmoticonHandler = (emoticonUrl) => {
      if (this.state.emoticonTimeoutID && selectedEmoticonUrl === emoticonUrl) {
        // double Click
        if (this.state.isChatSending) {
          return
        }

        this.setState({ isChatSending: true })

        clearTimeout(this.state.emoticonTimeoutID)
        this.setEmoticonTimeout(null)
        chattingStore.setSelectedEmoticon(emoticonUrl)
        sendChatMessage().catch(console.error).finally(() => {
          setTimeout(() => {
            this.setState({ isChatSending: false })
          }, this.emoticonDoubleClickTimeout)
        })
      } else {
        // one Click
        chattingStore.setSelectedEmoticon(emoticonUrl)
        if (this.state.emoticonTimeoutID) {
          clearTimeout(this.state.emoticonTimeoutID)
        }

        const timeout = setTimeout(() => {
          this.setEmoticonTimeout(null)
        }, this.emoticonDoubleClickTimeout)
        this.setEmoticonTimeout(timeout)
      }
    }

    const onClickCancelReply = () => {
      chattingStore.setReplyToUser(undefined)
    }

    const replyToUserElem = (
      <span className="chat-input--reply" style={{ height: '19px' }}>
        @ {replyToUser?.nick}
        <Button className="chat-input--reply--cancel-btn" onClick={onClickCancelReply}>
          <span className="cancel-ico"/>
        </Button>
      </span>
    )

    if (!chattingOptions.activeEmoticonAttach) {
      hideEmoticonsPanel()
    }

    if (!chattingOptions.activeBetHistoryAttach && !chattingOptions.activeImageAttach) {
      this.setState({
        visiblePicker: false,
        pickWrapClass: 'pick-wrap',
      })
    }

    let chatInputAreaClassName = 'chat-input-area'
    if (!isVisibleChattingModal && isMobile) {
      chatInputAreaClassName += ' close'
    }

    const onClickOpenChatSetting = () => {
      this.setState({
        bubbleVisible: false,
      })
      DialogUtils.showChatSetting()
    }

    let chattingInputClassName = 'chat-input--textarea'
    if (errorMessage) {
      chattingInputClassName += ' error'
    }

    const settingBtn = (
      <BettingHistoryQuickMenu className="no-border">
        <ul className="quick-menu-list">
          <li className="quick-menu-list--item">
            <Button className="history-quick-btn" onClick={onClickOpenChatSetting}>
              <SettingIco/>
              설정
            </Button>
          </li>
        </ul>
      </BettingHistoryQuickMenu>
    )

    // const onClickSelectLiveMatch = (rowData) => {
    //   globalStore.pushDialogOk({
    //     custom: <SelectLiveMatch/>,
    //     className: 'big-size box-type',
    //     singlePress: '확인',
    //     callbackCloseDialog: () => {
    //       globalStore.closeDialog()
    //     },
    //     // 뒷배경 클릭 시 닫히도록
    //     callbackCloseDialogBg: () => {
    //       globalStore.closeDialog()
    //     },
    //   })
    // }

    const onClickLoginHandler = () => {
      authStore.showLoginPopup(<Login/>)
    }

    const onClickChatTodayUserPickBtn = () => {
      if (!authStore.isSignedIn) {
        onClickLoginHandler()
      } else {
        logStore.writeButtonLog({ pageId: 'h010', buttonId: '003' }).catch(console.error)
        PopupUtils.showTodayUserPick()
      }
    }

    const onClickChatUserActivityScoreBtn = () => {
      if (!authStore.isSignedIn) {
        onClickLoginHandler()
      } else {
        PopupUtils.showUserActivityScore()
      }
    }

    const radius = 20
    const { dashOffset, circumference } = makeActivityGageConfig(radius)
    const progressBarName = classNames('progress-bar-wrap', {
      'acheive': globalStore.userActivityInfo?.activityRewardAvailable,
    })

    let touchTimer
    /** TODO 임시 적용. 영주부실장님 작업시 참고용. */
    const newUserPickIco = (<NewBadgeIco/>)

    const onClickCheerForTeamBtn = (): void => {
      if (!authStore.isSignedIn) {
        onClickLoginHandler()
      } else {
        if (!isCheerForTeamActive) return
        logStore.writeButtonLog({ pageId: 'h010', buttonId: '005' }).catch(console.error)
        globalStore.clearActivityScoreNoticeQueue()
        chattingStore.setVisibleCheerForTeamPanel(true)
      }
    }

    const onCloseCheerForTeam = (): void => {
      chattingStore.setVisibleCheerForTeamPanel(false)
    }

    let activityTypeEle
    const activityScoreNoticeQueue = globalStore.activityScoreNoticeQueue
    for (const userActivityInfo of activityScoreNoticeQueue) {
      const { activityType } = userActivityInfo
      activityTypeEle = activityType
    }
    const isMobileChatting = ((activityTypeEle === USER_ACTIVITY_TYPE.BET_HISTORY_SHARED_TO_CHAT)
      || (activityTypeEle === USER_ACTIVITY_TYPE.CHEER_FOR_TEAM)) && isMobile && chattingStore.isVisibleChattingModal

    const chatContentButtons = _.chain(chatContentButtonOptions)
      .reject(option => {
        return option.buttonType === CHAT_CONTENT_BUTTON_TYPE.CHEER_FOR_TEAM || !option.enabled
      })
      .sortBy(['order'])
      .map((option, index) => {
        if (option.buttonType === CHAT_CONTENT_BUTTON_TYPE.COMPANY_PICK && this.companyPickStore.notice) {
          return (
            <div className="chat-floating-btn-list" key={`chat-content-btn-${index}`}>
              <PickNoticePopup/>
            </div>
          )
        } else if (option.buttonType === CHAT_CONTENT_BUTTON_TYPE.TODAY_USER_PICK) {
          return (
            <AdvButton
              zIndex={1}
              img={TodayUserPickBtnImgUrl}
              badge={userPickStore.isNewBestPick && newUserPickIco}
              badgeTop={5}
              badgeRight={8}
              borderRadius={6}
              imgBorderRadius={6}
              css={ChatFloatingBtnStyle}
              className={!isMobile ? 'chat-floating-btn-list today-pick pc' : 'chat-floating-btn-list today-pick'}
              key={`chat-content-btn-${index}`}
              onClick={onClickChatTodayUserPickBtn}
            />
          )
        } else if (option.buttonType === CHAT_CONTENT_BUTTON_TYPE.USER_ACTIVITY_SCORE) {
          return (
            <div className="chat-floating-btn-list activity-score" key={`chat-content-btn-${index}`}>
              <Button
                css={ChatFloatingBtnStyle}
                onClick={onClickChatUserActivityScoreBtn}
              >
                {globalStore.userActivityInfo?.activityRewardAvailable &&
                  <NewBadgeIco/>
                }
                <div className={progressBarName}>
                  <svg width="42" height="42" xmlns="http://www.w3.org/2000/svg" className="progress-bar">
                    <defs>
                      <linearGradient id="progress-linear-gradient" x1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                        <stop offset="0" stopColor="#FB9F00"/>
                        <stop offset="1" stopColor="#EA5407"/>
                      </linearGradient>
                    </defs>
                    <circle cx="21" cy="21" r={radius} fill="none" className="bg"/>
                    <circle cx="21" cy="21" r={radius} fill="none" strokeDasharray={circumference} strokeDashoffset={dashOffset}
                     className="processing"/>
                  </svg>
                </div>
                <div className="activity-score-btn">
                  <img className="activity-score-btn-img" src={ActivityScoreIconImgUrl} alt="활동점수 팝업버튼 아이콘"/>
                </div>
              </Button>
              {isMobileChatting &&
                <div className="activity-popup-wrap">
                  <ActivityScoreNoticePopup/>
                </div>
              }
            </div>
          )
        }

        return (<Fragment key={`chat-content-btn_${index}`}/>)
      }).value()

    const bettingHistoryQuickMenuName = classNames('betting-history-quick-menu-btn', {
      'pc': !isMobile,
      'is-popular-searches': (authStore?.userInfo?.chatOption?.['showPopularSearchTerm'] ?? true) && _.size(popularSearchTerms) > 0,
    })
    const cheerBtnName = classNames('', {
      'pc': !isMobile,
      'is-popular-searches': (authStore?.userInfo?.chatOption?.['showPopularSearchTerm'] ?? true) && _.size(popularSearchTerms) > 0,
    })
    const chatFloatingBtnName = classNames('chat-floating-btn', {
      'is-popular-searches': (authStore?.userInfo?.chatOption?.['showPopularSearchTerm'] ?? true) && isMobile && _.size(popularSearchTerms) > 0,
    })


    let chatContents = (
      <Fragment>
        {/* 인기 검색어 */}
        {isMobile && (authStore?.userInfo?.chatOption?.['showPopularSearchTerm'] ?? true) &&
          <PopularSearchTerm className="chatting" showInChat isScrollCloseEnabled={false}/>
        }
        {/* 채팅 탭 및 내용 */}
        <ChatTab
          key="Chat:ChatTab"
          showKeyboard={showKeyboard}
          hideEmoticonPanelHandler={hideEmoticonsPanel}
          chatInputAreaRef={this.chatInputAreaRef}
          chatInputRef={this.chatInputRef}/>
        {/*{isMobile && chattingOptions.activeLiveScore && (*/}
        {/*  <Button TP css={SelectLiveMatchBtnStyle} onClick={onClickSelectLiveMatch}>*/}
        {/*    <img src={ChatLiveBtnImg} alt="실시간 라이브 설정 버튼"/>*/}
        {/*  </Button>*/}
        {/*)}*/}

        {/* 승부사카지노 배너 */}
        <AdvCasinoBannerButton className={isMobile ? 'chat-banner' : 'pc-chat-banner'}/>

        {/*채팅 설정 버튼*/}
        {isMobile && isVisibleChattingModal &&
          <Popover
            placement="bottomRight"
            visible={bubbleVisible}
            onVisibleChange={onVisibleChange}
            content={settingBtn}
            trigger="click"
            overlayClassName="betting-history-quick-menu"
            getPopupContainer={() => document.getElementById('chatWrap')}
          >
            <Button className={bettingHistoryQuickMenuName} css={ChatQuickSpotsMenuBtnStyle}>
              <span className="spots"/>
            </Button>
          </Popover>
        }

        {!isMobile &&
          <Popover
            placement="bottomRight"
            visible={bubbleVisible}
            onVisibleChange={onVisibleChange}
            content={settingBtn}
            trigger="click"
            overlayClassName="betting-history-quick-menu"
            getPopupContainer={() => document.getElementById('chatWrap')}
          >
            <Button className="betting-history-quick-menu-btn" css={ChatQuickSpotsMenuBtnStyle}>
              <span className="spots"/>
            </Button>
          </Popover>
        }

        {/* 응원글 패널 출력 버튼 */}
        {isCheerForTeamActive
          && isChatCheerForTeamButtonEnabled
          && !isVisibleCheerForTeamPanel
          && (
          <AdvButton
            zIndex={2}
            height={34}
            img={cheerBtnImgUrl}
            css={ChatFavoriteMatchBtnStyle}
            onClick={onClickCheerForTeamBtn}
            className={cheerBtnName}
          />
        )}


        {!isMobile &&
          // 이모티콘 영역 PC버전
          <EmoticonArea className={emoticonAreaClass}>
            {/* *************** 업로드 이미지/이모트콘/베팅내역 미리보기 PC버전 *************** */}
            {previewWrap}
            {/* 이모티콘 높이는 키패드 높이와 동일해야합니다.(현재 더미 높이) */}
            <ul className="emoticon-list" css={isMobile ? { height: '288px' } : { height: '170px' }}>
              {emoticonList.map((emoticon, index) =>
                <li key={`emoticon-area-list-item_${emoticon.pvImg + index}`} className="emoticon-list--item">
                  <Button onClick={() => onClickEmoticonHandler(emoticon.img)} className="emoticon-btn">
                    <img src={emoticonUrlPrefix + emoticon.pvImg} alt={`이모티콘 ${index}`}/>
                  </Button>
                </li>,
              )}
            </ul>
          </EmoticonArea>
        }

        {/* 채팅 입력창 영역 */}
        <div ref={this.chatInputAreaRef} className={chatInputAreaClassName}>
          {/* *************** 업로드 이미지/이모트콘/베팅내역 미리보기 *************** */}
          {isMobile && previewWrap}

          {/* + 버튼 */}
          {isMobile && plusQuickMenuBtn}

          {/* *************** + 버튼 리스트 *************** */}
          {isMobile &&
            <div className={pickWrapClass}>
              {visiblePicker && quickMenuListItems}
            </div>
          }

          {/* *************** 하단 채팅 입력창 란 *************** */}
          <div className="chat-input">
            {/* 답장할 닉네임 */}
            {replyToUser && replyToUserElem}
            <TextArea
              id="chatting-input-textarea"
              key={chattingInputKey}
              placeholder={authStore.isSignedIn ? '대화내용을 입력해주세요' : '로그인 후 이용할 수 있습니다.'}
              autoSize={{ minRows: 1, maxRows: 3 }}
              className={chattingInputClassName}
              value={textValues}
              ref={this.chatInputRef}
              onClick={() => {
                if (!authStore.isSignedIn) {
                  authStore.showLoginPopup(<Login/>)
                  return
                }
              }}
              onFocus={(e) => {
                if (!authStore.isSignedIn) {
                  authStore.showLoginPopup(<Login/>)
                  return
                }
                if (isMobile && !this.state.showKeyboard) {
                  e.target.blur()
                  // 이모티콘 창이 열린 상태에서 채팅 리스트 스크롤 후 댓글입력창 클릭 시 채팅 리스트가 위로 올라가있는 현상 해결
                  if (isShowEmoticonPanel()) {
                    chattingStore.setDelayChatAreaScrollToBottom()
                  }

                  hideEmoticonsPanel()
                  globalStore.clearActivityScoreNoticeQueue()
                  this.setState({ showKeyboard: true })
                }
              }}
              onBlur={() => this.setState({ showKeyboard: false })}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (
                  (authStore?.userInfo.chatOption.sendMessageToEnter === SendMessageToEnterType.M_ON_PC_ON)
                  || (isMobile
                    && authStore?.userInfo.chatOption.sendMessageToEnter === SendMessageToEnterType.M_ON_PC_OFF)
                  || (!isMobile
                    && authStore?.userInfo.chatOption.sendMessageToEnter === SendMessageToEnterType.M_OFF_PC_ON)
                ) {
                  if (!e.shiftKey && e.keyCode === 13) {
                    e.preventDefault()
                    onClickSendChatMessage()
                  }
                } else if (e.ctrlKey && e.keyCode === 13) {
                  e.preventDefault()
                  onClickSendChatMessage()
                }
              }}
              onTouchStart={(e) => {
                touchTimer = Date.now()
              }}
              onTouchEnd={() => {
                if ((Date.now() - touchTimer) > 500) {
                  document.execCommand('paste')
                }
              }}
              onChange={e => onTextAreaChange(e.target.value)}
            />
            {isMobile && chattingOptions.activeEmoticonAttach && emoticonBtn}
          </div>

          {/* *************** 채팅 보내기 버튼 *************** */}
          {isMobile && sendBtn}

          {/* *************** PC 버전 하단 퀵메뉴 버튼 리스트 *************** */}
          {!isMobile &&
            <div className="chat-btn-list">
              {(chattingOptions.activeBetHistoryAttach || chattingOptions.activeImageAttach) && quickMenuListItems}
              {chattingOptions.activeEmoticonAttach && emoticonBtn}
              {sendBtn}
            </div>
          }

          {/* *************** 이모티콘 영역 *************** */}
          {isMobile &&
            <EmoticonArea className={emoticonAreaClass}>
              {/* 이모티콘 높이는 키패드 높이와 동일해야합니다.(현재 더미 높이) */}
              <ul className="emoticon-list" css={isMobile ? { height: '288px' } : { height: '150px' }}>
                {emoticonList.map((emoticon, index) =>
                  <li key={`emoticon-area-list-item_${emoticon.pvImg + index}`} className="emoticon-list--item">
                    <Button onClick={() => onClickEmoticonHandler(emoticon.img)} className="emoticon-btn">
                      <img src={emoticonUrlPrefix + emoticon.pvImg} alt={`이모티콘 ${index}`}/>
                    </Button>
                  </li>,
                )}
              </ul>
            </EmoticonArea>
          }

          {/* *************** 응원글 영역 PC MO *************** */}
          {isVisibleCheerForTeamPanel && (
            <CheerForTeam isEmoticon={isShowEmoticonPanel()} onCloseCheerForTeam={onCloseCheerForTeam}/>
          )}
        </div>

        {!authStore.isSignedIn && (
          <Button TP css={ChatLoginBtn} className={isMobile ? 'input-btn' : 'input-btn pc-input-btn'} onClick={onClickLoginHandler}/>
        )}

        {isMobile && chatToggleBtnClass !== this.CLOSE_CHAT_WRAP_CLASS && (
          <ModalBg onClick={onClickToggleChat} className="modal-bg"/>
        )}

        <div className={chatFloatingBtnName}>
          { !isVisibleCheerForTeamPanel && chatContentButtons }
        </div>

      </Fragment>
    )

    let chatLiveScoreContents = <ChatLiveScore/>

    if (isMobile && chatToggleBtnClass === this.CLOSE_CHAT_WRAP_CLASS) {
      chatContents = null
      chatLiveScoreContents = null
    }

    let chatToggleBtnIconClassName = 'open-chat'
    if (isMobile && !isVisibleChattingModal && isChatActive) {
      if (newChatMessage) {
        chatToggleBtnIconClassName += ' active'
      }
    }

    if (this.state.showKeyboard) {
      chatToggleBtnIconClassName += ' open-keyboard'
    }

    return (
      <ChatWrap id="chatWrap" className={isMobile ? chatWrapClass : pcChatWrapClass}>
        {/* 채팅 토글 버튼 */}
        {isMobile && isChatActive &&
          <Fragment>
            {chattingOptions.activeLiveScore && chatLiveScoreContents}
            <ChatToggleBtn className={`${chatToggleBtnClass} ${chatBtnClass}`} onClick={onClickToggleChat}>
              {/* 채팅 활성화 중일 경우 className 'active' 추가 */}
              <div className={chatToggleBtnIconClassName}>

                <img src={chatIconImgUrl} alt="실시간 라이브 채팅" className="chat-icon"/>
                <span className="favorite-pulse"/>
                <span className="favorite-pulse pulse-2"/>

              </div>

              <div className="close-chat">
                <span className="x-ico"/>
              </div>
            </ChatToggleBtn>
          </Fragment>
        }

        {chatContents}
        <MessageContentPopup
          visible={visibleSendMessagePopup}
          setVisible={(show) => globalStore.setVisibleSendMessagePopup(show)}
          receive={replyTargetUser}
          action="send"
          useRequestWaitTimer
          fromViewType={FROM_VIEW_TYPE.CHAT}
        />
      </ChatWrap>
    )
  }
}

interface WrapperProps {
  pcChatWrapClass?: string
  chatBtnClass?: string
}

const
  Wrapper: React.FC<WrapperProps> = (props: WrapperProps) => {
    const {
      globalStore, authStore, chattingStore, messageStore, userStore,
      companyPickStore, bettingHistoryStore, userPickStore, penaltyStore,
      logStore, popularSearchTermStore,
    } = useRootStore()
    const { pcChatWrapClass, chatBtnClass } = props
    const listProps = {
      chattingStore,
      authStore,
      globalStore,
      messageStore,
      companyPickStore,
      pcChatWrapClass,
      chatBtnClass,
      bettingHistoryStore,
      userPickStore,
      penaltyStore,
      userStore,
      logStore,
      popularSearchTermStore,
    }
    return <Chat {...listProps} />
  }

export default Wrapper
